import DeleteIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ReactComponent as EditIcon } from "../../../common/icons/editFilled.svg";

import { isFulfilled } from "@reduxjs/toolkit";
import { epochToFormatDate } from "../../../app/common";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { fetchTesPriceList } from "../../settings/settingsActions";
import { SupplementPriceOverride } from "../../talents/talentAPI";
import {
  priceListUnits,
  PriceValueType,
  SingleOverrideItem,
  TesPricingOverrideItems,
  TesPricingOverrideOptions,
} from "../tesPricingOverrideAPI";
import EditTesPricingOverrideModal from "./EditTesPricingOverrideModal";

interface Props {
  tesId?: string;
  date: number;
  isLoading: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  tesPricingOverrideItems: { [key: string]: SupplementPriceOverride };
  onEdit: (item: SingleOverrideItem) => Promise<boolean> | boolean;
  onAdd: (item: SingleOverrideItem) => Promise<boolean> | boolean;
  onDelete: (key: string) => void;
  modalTitle: string;
  modalItemSelectLabel: string;
  modalItemValueLabel: string;
  tableWidth?: string;
  editable?: boolean;
  canEdit?: boolean;
}

const DRAG_HANDLE_SELECTOR = "drag-handle-tesPricingOverrides";

const adjustForMultiplier = (
  value: string | undefined,
  unit: string | undefined,
): string => {
  if (!value || !unit) return "";
  return unit === "SALARY_MULTIPLIER"
    ? (Number(value) * 100).toString()
    : value;
};

const TesPricingOverride = ({
  tesId,
  date,
  isLoading,
  setIsLoading,
  tesPricingOverrideItems = {},
  onEdit,
  onAdd,
  onDelete,
  modalTitle,
  modalItemSelectLabel,
  modalItemValueLabel,
  tableWidth,
  editable,
  canEdit = true, // TODO: remove default value or change to false after access rights are handled in every view
}: Props) => {
  const [originalItem, setOriginalItem] = useState<SingleOverrideItem | null>(
    null,
  );

  const translatedSupplementNames = useAppSelector(
    (state) => state.localized.supplement,
  );

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [tesPricingOverrideOptions, setTesPricingOverrideOptions] =
    useState<TesPricingOverrideOptions>({});

  useEffect(() => {
    if (!tesId || !date) return;
    setIsLoading && setIsLoading(true);

    dispatch(
      fetchTesPriceList({
        tesId: tesId,
        date: epochToFormatDate(date, "yyyy-MM-dd"),
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const combinedPrices: TesPricingOverrideOptions = action.payload.reduce(
          (acc, item) => {
            Object.entries(item.prices).forEach(([key, priceDetails]) => {
              if (priceDetails.unitValueOverrideAllowed) {
                const unitValue = adjustForMultiplier(
                  priceDetails.unitValue,
                  priceDetails.unit,
                );
                acc[key] = {
                  unit:
                    (priceDetails.unit as PriceValueType) || priceDetails.unit, // Retain original unit if it doesn't match PriceValueType
                  unitValue,
                };
              }
            });
            return acc;
          },
          {} as TesPricingOverrideOptions,
        );

        setTesPricingOverrideOptions(combinedPrices);
      }
      setIsLoading && setIsLoading(false);
    });
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tesId, date, dispatch]);

  const handleOnSave = async (item: SingleOverrideItem) => {
    try {
      const modifiedItem = {
        ...item,
        // If unit is SALARY_MULTIPLIER, divide the value by 100
        value:
          item.unit === "SALARY_MULTIPLIER"
            ? (Number(item.value) / 100).toString()
            : item.value,
      };

      // If key already exists, edit it. Otherwise call the add function.
      const isSuccess =
        originalItem?.key === item.key
          ? await onEdit(modifiedItem)
          : await onAdd(modifiedItem);

      if (isSuccess) {
        // Closes modal
        setOriginalItem(null);
      } else {
        // Error message comes from main component
      }
    } catch (error) {
      // Error message comes from main component
    }
  };

  const showAddButton = editable !== false && canEdit;

  const customTesPricingOverrideItems = useMemo(() => {
    // Sort keys by translated name or key
    const sortedKeys = Object.keys(tesPricingOverrideItems).sort((a, b) => {
      const nameA = translatedSupplementNames[a] || a;
      const nameB = translatedSupplementNames[b] || b;
      return nameA.localeCompare(nameB);
    });

    // Reconstruct the sorted object
    return sortedKeys.reduce((acc, key) => {
      acc[key] = {
        value: adjustForMultiplier(
          tesPricingOverrideItems[key].value,
          tesPricingOverrideItems[key].unit,
        ),
        unit: tesPricingOverrideItems[key].unit,
      };
      return acc;
    }, {} as TesPricingOverrideItems);
  }, [tesPricingOverrideItems, translatedSupplementNames]);

  // Filter out the options that are already added
  const filteredOptions = Object.keys(tesPricingOverrideOptions)
    .filter((key) => !tesPricingOverrideItems[key])
    .reduce((acc, key) => {
      acc[key] = tesPricingOverrideOptions[key];
      return acc;
    }, {} as TesPricingOverrideOptions);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: tableWidth || "unset",
      }}
    >
      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={{ padding: "6px" }}>
                {t("tesPricingOverride:tesPricingOverride")}
              </TableCell>
              <TableCell sx={{ padding: "6px" }}>
                {t("tesPricingOverride:selectValueLabel")}
              </TableCell>
              <TableCell sx={{ padding: "6px" }} />
              <TableCell sx={{ padding: "6px" }} />
            </TableRow>
            {Object.keys(customTesPricingOverrideItems).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t("tesPricingOverride:noTesPricingOverrideItemsToShow")}
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(customTesPricingOverrideItems).map(
                ([key, item], index) => (
                  <TableRow key={key + index}>
                    <TableCell sx={{ fontWeight: 600, padding: "6px" }}>
                      {translatedSupplementNames[key] || key}
                    </TableCell>
                    <TableCell sx={{ padding: "6px" }}>
                      <Box>
                        {`${item.value} ${priceListUnits[item.unit] || ""}`}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: "6px" }}>
                      {editable && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            color: "rgba(0, 0, 0, 0.52)",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setOriginalItem({
                              key: key,
                              value: item.value,
                              unit: item.unit,
                            })
                          }
                        >
                          <EditIcon style={{ marginRight: "10px" }} />
                          {t("common:edit")}
                        </Box>
                      )}
                    </TableCell>

                    <TableCell sx={{ padding: "6px" }}>
                      {editable !== false && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            color: "rgba(0, 0, 0, 0.52)",
                            cursor: "pointer",
                          }}
                          onClick={() => onDelete(key)}
                        >
                          <DeleteIcon
                            sx={{
                              marginRight: "5px",
                            }}
                          />
                          {t("common:delete")}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box minHeight="16px">
        {showAddButton &&
          (!isLoading ? (
            <AddButton
              variant="outlined"
              sx={{
                margin: "16px 0px 32px 0px",
                alignSelf: "flex-start",
              }}
              label={t("tesPricingOverride:addTesPricingOverrideItem")}
              onClick={() => setOriginalItem({ key: "", value: "", unit: "" })}
              // Has to have options to be enabled
              disabled={Object.keys(filteredOptions).length === 0}
            />
          ) : (
            <Button
              variant="outlined"
              disabled={true}
              sx={{
                margin: "16px 0px 32px 0px",
                borderRadius: "15px",
              }}
            >
              <CircularProgress
                size={20}
                sx={{
                  margin: "0px 16px",
                }}
              />
            </Button>
          ))}
      </Box>
      <DialogComponent
        open={!!originalItem && !!canEdit}
        onClose={() => setOriginalItem(null)}
        autoWidth
        DialogContentProps={{ sx: { padding: "0px 24px" } }}
        dragHandleSelector={DRAG_HANDLE_SELECTOR}
      >
        <DialogContentWrapper>
          <EditTesPricingOverrideModal
            tesPricingOverrideOptions={filteredOptions}
            originalItem={originalItem}
            isLoading={isLoading}
            handleOnSave={handleOnSave}
            onClose={() => setOriginalItem(null)}
            modalTitle={modalTitle}
            modalItemSelectLabel={modalItemSelectLabel}
            modalItemValueLabel={modalItemValueLabel}
          />
        </DialogContentWrapper>
      </DialogComponent>
    </Box>
  );
};

export default TesPricingOverride;
